<script setup lang="ts">
const props = defineProps<{
  setNid: number
  productLineTitle?: string | null
}>()

const itemsPerPage = ref(8)
const page = ref(1)

const {
  data: prebuiltConfigurations,
  status,
  refresh,
} = await useLazyAsyncData(
  async () => {
    return await createPrebuiltConfigurationsRepository().getBySetNid(
      props.setNid,
      {
        page: page.value,
        itemsPerPage: itemsPerPage.value,
      }
    )
  },
  { default: defaultPaginationObject }
)

const open = ref(false)
const variantsElement = ref<HTMLElement | null>(null)

async function toggleOpen() {
  if (!open.value) {
    open.value = true
  } else {
    if (page.value !== 1) {
      page.value = 1
      await refresh()
    }
    open.value = false
    if (variantsElement.value) {
      scrollToElementWithOffset(variantsElement.value, 150)
    }
  }
}

watch(
  () => page.value,
  async () => {
    await refresh()
    if (variantsElement.value) {
      scrollToElementWithOffset(variantsElement.value, 150)
    }
  }
)

const emit = defineEmits<{
  (event: 'configure', nid: number): void
}>()
</script>

<template>
  <UiSection
    v-if="status !== 'error' && prebuiltConfigurations?.items.length"
    id="variants"
    :title="$t('configurator.prebuilt.title')"
    class="!pb-0"
  >
    <div ref="variantsElement">
      <UiSkeletonProductsGrid v-if="status === 'pending'" :count="4" />
      <template v-else>
        <div class="overflow-hidden border-y-4 border-neutral-200">
          <div
            class="-mb-1 -mr-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4"
          >
            <ProductPrebuiltCard
              v-for="prebuilt in prebuiltConfigurations.items.slice(
                0,
                open ? undefined : 4
              )"
              :key="prebuilt.nid"
              :prebuilt="prebuilt"
              :product-line-title="props.productLineTitle || ''"
              class="border-b-4 border-r-4 border-neutral-200 p-5"
              @configure="emit('configure', $event)"
            />
          </div>
        </div>
        <div class="flex justify-end pr-6 pt-5">
          <UiPagination
            v-show="open && prebuiltConfigurations.pageCount > 1"
            v-model:value="page"
            :items-per-page="itemsPerPage"
            :item-count="prebuiltConfigurations.itemCount"
            :loading="false"
          />
        </div>
        <div
          class="flex w-full justify-center pt-4"
          :class="{ 'sticky bottom-5': open }"
        >
          <div class="cursor-pointer pb-1" @click="toggleOpen">
            <template v-if="!open && prebuiltConfigurations.pageCount > 1">
              <UiAnimatedUnderlineText>
                {{ $t('configurator.prebuilt.showAll') }}
              </UiAnimatedUnderlineText>
            </template>
            <template v-if="open">
              <div
                class="group flex items-center gap-x-2 rounded-full bg-neutral-100 p-3 transition-all hover:bg-neutral-200"
              >
                <Icon
                  name="fa6-solid:chevron-up"
                  class="ml-1 transition-all group-hover:mb-1"
                  size="15"
                />
                <div>
                  {{ $t('configurator.prebuilt.showLess') }}
                </div>
              </div>
            </template>
          </div>
        </div>
      </template>
    </div>
  </UiSection>
</template>
